/**
 * Shared fiscal time-period derivations for Navigator Reports.
 *
 * The app only stores `{ period, week, year }` in Redux (`appData.periodWeek.data`).
 * Anything else the UI or API payloads need (fiscal quarter, week-in-year) must be
 * derived from those values. Keeping the derivations here means the filters slice
 * and the summary builder can never disagree about what the selected period means.
 */

/** Number of weeks in a standard fiscal period. */
const WEEKS_PER_PERIOD = 4;

/** Fiscal quarter boundaries: Q1 = P1–P3, Q2 = P4–P6, Q3 = P7–P9, Q4 = P10–P13. */
const QUARTER_LAST_PERIOD: readonly number[] = [3, 6, 9, 13];

/** Total number of fiscal periods in a year. */
export const PERIODS_PER_YEAR = 13;

/**
 * Derive the fiscal quarter (1–4) that contains the given fiscal period.
 * Returns `undefined` when the period is missing or out of range so callers can
 * decide their own fallback instead of receiving a misleading quarter.
 */
export const deriveFiscalQuarter = (period?: number | null): number | undefined => {
  if (typeof period !== 'number' || !Number.isFinite(period)) {
    return undefined;
  }

  if (period < 1 || period > PERIODS_PER_YEAR) {
    return undefined;
  }

  const index = QUARTER_LAST_PERIOD.findIndex((lastPeriod) => period <= lastPeriod);
  return index === -1 ? undefined : index + 1;
};

/**
 * Convert a fiscal period + week-in-period pair into an absolute week-in-year.
 * Returns `undefined` when either input is missing/invalid.
 */
export const deriveWeekInYear = (period?: number | null, weekInPeriod?: number | null): number | undefined => {
  if (typeof period !== 'number' || typeof weekInPeriod !== 'number') {
    return undefined;
  }

  if (period < 1 || weekInPeriod < 1) {
    return undefined;
  }

  return (period - 1) * WEEKS_PER_PERIOD + weekInPeriod;
};
